/** @format */

import { ActivatedRouteSnapshot, Router } from '@angular/router';
import {
	CriteoService,
	EMPTY_CRITEO_VIEW_PRODUCTS_RESPONSE
} from '@shared/data-access/product-recommendations';
import { Injectable, PLATFORM_ID, REQUEST_CONTEXT, computed, inject } from '@angular/core';
import { KillswitchService, Logger } from '@shared/data-access/services';
import {
	ProductListApiService,
	ProductListCompareService,
	ProductListMlService,
	ProductListUIService
} from '@plp/data-access/services';
import { catchError, map, take } from 'rxjs/operators';
import { isPlatformBrowser, isPlatformServer } from '@angular/common';
import {
	productListResolverFeatureFlagsSet,
	selectFeatureFlags,
	setAllFilters,
	setCriteoProducts,
	setInitialPageNumber,
	setInitialPageSize,
	setIsProductListPageLoading,
	setPinnedContent,
	setProductList,
	setSelectedSort
} from '@plp/data-access/state';

import { ProductList } from '@plp/data-access/interfaces';
import { ProductTypes } from '@shared/data-access/interfaces';
import { SeoService } from '@seo/data-access';
import { Store } from '@ngrx/store';
import { Title } from '@angular/platform-browser';
import { of } from 'rxjs';
import { searchDropdownValues } from '@plp/data-access/constants';
import { setCommonPageType } from '@state/data-access';

@Injectable()
export class SearchResultsListPageResolveGuard {
	private readonly log = inject(Logger);

	private readonly productListApiService = inject(ProductListApiService);

	private readonly productListUIService = inject(ProductListUIService);

	private readonly killSwitchService = inject(KillswitchService);

	private readonly router = inject(Router);

	private readonly seoService = inject(SeoService);

	private readonly store = inject(Store);

	private readonly title = inject(Title);

	private readonly plpCompareService = inject(ProductListCompareService);

	private readonly plpMlService = inject(ProductListMlService);

	private readonly criteoService = inject(CriteoService);

	private readonly platformId = inject(PLATFORM_ID);

	private readonly propagateToServer = inject<{
		PROPAGATE_ERROR_TO_SERVER: (propagatedError: {
			errorType: string;
			redirectUrl?: string;
		}) => { errorType: string; redirectUrl?: string };
	}>(REQUEST_CONTEXT, { optional: true })?.PROPAGATE_ERROR_TO_SERVER;

	private readonly featureFlags = this.store.selectSignal(selectFeatureFlags);

	private readonly isUsingCriteoSPAAdServer = computed(
		() => this.featureFlags().isUsingCriteoSPAAdServer
	);

	showPinnedContent = false;

	showPinnedProducts = true;

	fulfillmentFacetsEnabled = false;

	constructor() {
		this.store.dispatch(setCommonPageType({ pageType: 'srlp' }));
		this.killSwitchService
			.getAllKillSwitchesForCurrentChain$()
			.pipe(take(1))
			.subscribe((data) => {
				this.store.dispatch(productListResolverFeatureFlagsSet({ featureFlags: data }));
				this.showPinnedContent = data.showPinnedContent;
				this.showPinnedProducts = data.showPinnedProducts;
				this.fulfillmentFacetsEnabled = data.fulfillmentFacetsEnabled;
			});
	}

	resolve(route: ActivatedRouteSnapshot) {
		this.title.setTitle('Search Results');

		const invalidQueryParams = this.productListApiService.searchRequestParamValidator(
			route.queryParams
		);

		const selectedSortNumber: number = parseInt(route.queryParams.selectedSort, 10);

		if (!invalidQueryParams) {
			const selectedSort: { label: string; value: number } = route.queryParams?.selectedSort
				? {
						label: searchDropdownValues[selectedSortNumber].label,
						value: selectedSortNumber
					}
				: { label: searchDropdownValues[0].label, value: 0 };
			this.store.dispatch(setSelectedSort({ selectedSort }));

			const pageNumber = this.productListUIService.getPageNumber(
				route.queryParams?.pageNumber
			);
			const pageSize = this.productListUIService.getPageSize(route.queryParams?.pageSize);
			const filters = this.productListApiService.formatFiltersFromURL(
				route.queryParams.filterFacets
			);
			if (filters) {
				this.store.dispatch(setAllFilters({ selectedFilters: filters }));
			}
			this.store.dispatch(setInitialPageNumber({ pageNumber }));
			this.store.dispatch(setInitialPageSize({ pageSize }));
			this.seoService.createRobotsTag({ value: 'Y' }, { value: 'N' }, { value: 'Y' });

			const queryParams = this.productListApiService.setProductListParamDataForApi(
				null,
				route.queryParams.searchTerm,
				pageNumber,
				filters,
				pageSize,
				this.fulfillmentFacetsEnabled,
				route.queryParams.selectedStore,
				route.queryParams.zipCode
			);

			return this.productListApiService
				.getProductListDataFromApi$(queryParams, 1, this.showPinnedProducts)
				.pipe(
					take(1),
					map((productListData: ProductList | null) => {
						this.handlePageRedirect(productListData);

						if (typeof productListData?.totalCount === 'undefined') {
							this.store.dispatch(
								setIsProductListPageLoading({ isProductListPageLoading: false })
							);
							return null;
						}
						this.plpMlService.generateNewMlSearchEventParentUUID();
						this.store.dispatch(setProductList({ productList: productListData }));
						if (!this.plpCompareService.hasTheComparePaintedDoorTestBeenOpened()) {
							this.plpCompareService.getCompareListFromSessionStorage();
						}

						if (this.showPinnedContent) {
							this.productListUIService.setPinnedContentHomrDataToState(
								productListData,
								route.queryParams.searchTerm.replace(/ /g, '_')
							);
						} else {
							this.store.dispatch(setPinnedContent({ pinnedContent: {} }));
						}

						if (this.isUsingCriteoSPAAdServer()) {
							// Use Criteo
							this.getAndSetCriteoProducts(
								route.queryParams.searchTerm,
								pageNumber,
								pageSize,
								filters
							);
						} else {
							this.store.dispatch(
								setCriteoProducts({
									criteoProducts: EMPTY_CRITEO_VIEW_PRODUCTS_RESPONSE
								})
							);
						}

						return productListData;
					}),
					catchError((error) => {
						if (isPlatformServer(this.platformId) && error?.errorType === 'redirect') {
							throw error;
						}

						this.log.error(
							`Search API error for search term ${
								route.queryParams.searchTerm
							} with the following error: ${this.getErrorToPrint(error)}`
						);
						this.store.dispatch(setProductList({ productList: null }));
						this.store.dispatch(
							setIsProductListPageLoading({ isProductListPageLoading: false })
						);
						return of(null);
					})
				);
		} else {
			this.router.navigateByUrl('/404', { skipLocationChange: false });
		}
	}

	private getAndSetCriteoProducts(
		searchTerm: string,
		pageNumber: number,
		listSize: number,
		filters = null
	): void {
		if (!isPlatformBrowser(this.platformId)) {
			this.store.dispatch(
				setCriteoProducts({ criteoProducts: EMPTY_CRITEO_VIEW_PRODUCTS_RESPONSE })
			);
			return;
		}
		const criteoFilters =
			filters && Object.keys(filters).length > 0
				? this.criteoService.parseFilters(filters)
				: null;
		this.criteoService
			.viewProductsPage({
				productType: ProductTypes.VIEW_SEARCH_RESULT,
				category: searchTerm,
				listSize,
				pageNumber,
				filters: criteoFilters
			})
			.subscribe({
				next: (res) => {
					this.criteoService.fireBeacon(res?.placements[0]?.carousel[0]?.OnLoadBeacon);
					this.store.dispatch(setCriteoProducts({ criteoProducts: res }));
				},
				error: (err) => {
					console.error('Error fetching Criteo data:', err);
					this.store.dispatch(
						setCriteoProducts({ criteoProducts: EMPTY_CRITEO_VIEW_PRODUCTS_RESPONSE })
					);
				}
			});
	}

	// Future resiliency if we move Search to SSR, we want to be able to abort render early for a redirect
	handlePageRedirect(productListData: ProductList | null): void {
		if (isPlatformBrowser(this.platformId) || !productListData?.searchVO?.landingPageURL) {
			return;
		}

		this.propagateToServer({
			errorType: 'redirect',
			redirectUrl: productListData.searchVO.landingPageURL
		});
		throw { errorType: 'redirect', redirectUrl: productListData.searchVO.landingPageURL };
	}

	/**
	 * @description
	 * Accepts an error and stringifies the important parts
	 */
	private getErrorToPrint(error): string {
		try {
			return JSON.stringify(error, ['status', 'message', 'arguments', 'type', 'name']);
		} catch {
			return '';
		}
	}
}
